import { IPricing, IAgreementInfo, OwnPricingApiClient } from 'api/OwnPricingApiClient'
import { BusyIndicator, DPFormField, DPInputNumber, PageHeader, useToast } from '@framework'
import { useSession } from '@hooks/useSession'
import { useTranslation } from '@hooks/useTranslation'
import { useApiClient } from '@hooks/useApiClient'
import { Formik } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import { Form, Message, Icon } from 'semantic-ui-react'
import * as Yup from 'yup'
import styles from './OwnPricingPage.module.css'
import useAgreementPricing from '../../../infrastructure/hooks/useAgreementPricing'
import useAgreementPricingByAgreement from '../../../infrastructure/hooks/useAgreementPricingByAgreement'
import { AgreementCode, DPButton } from 'shared'
import DPTable from 'infrastructure/framework/DPForms/DPTable'

function AgreementPricingRow({
    dealerId,
    agreement,
    categoryPricing,
    onBusyChange,
    validationSchema,
    isPricingUnchanged,
    isSaveButtonDisable,
    canBeDeleted,
}: {
    dealerId: number
    agreement: IAgreementInfo
    categoryPricing: IPricing | undefined
    onBusyChange: (agreementId: number, busy: boolean) => void
    validationSchema: Yup.AnyObjectSchema
    isPricingUnchanged: (origin: IPricing, current: IPricing) => boolean
    isSaveButtonDisable: (isPricingUnchanged: boolean, hasError: boolean) => boolean
    canBeDeleted: (pricing: IPricing) => boolean
}) {
    const hook = useAgreementPricingByAgreement(dealerId, agreement.id)
    const toast = useToast()
    const $t = useTranslation('OwnPricingPage')
    const isPurchaseContractWithoutSalesCommission =
        agreement.code === AgreementCode.PurchaseContractWithoutSalesCommission
    const isPurchaseContract100 = agreement.code === AgreementCode.PurchaseContract100
    const disableCommitmentCommission = isPurchaseContractWithoutSalesCommission
    const disableDocumentFee =
        isPurchaseContractWithoutSalesCommission || isPurchaseContract100

    useEffect(() => {
        onBusyChange(agreement.id, hook.isBusy)
    }, [hook.isBusy, agreement.id, onBusyChange])

    useEffect(() => {
        return () => onBusyChange(agreement.id, false)
    }, [agreement.id, onBusyChange])

    const pricing = hook.useCustom ? (hook.pricing ?? categoryPricing) : categoryPricing

    if (!pricing) {
        return null
    }

    const initialValues: IPricing = {
        ...pricing,
        dealerCommitmentCommissionMin: disableCommitmentCommission ? 0 : pricing.dealerCommitmentCommissionMin,
        dealerCommitmentCommissionPct: disableCommitmentCommission ? 0 : pricing.dealerCommitmentCommissionPct,
        documentFee: disableDocumentFee ? 0 : pricing.documentFee,
    }

    return (
        <Formik<IPricing>
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            validateOnMount
            onSubmit={() => {}}
        >
            {formik => (
                <tr className={styles['agreement-row']}>
                    <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '16px' }}>
                            <span className={styles['add-icon-slot']}>
                                {!hook.useCustom && (
                                    <Icon
                                        name="plus"
                                        className={styles['add-icon']}
                                        title={$t('addOwnPricing')}
                                        onClick={() => hook.setUseCustom(true)}
                                    />
                                )}
                            </span>
                            <span>{agreement.name}</span>
                        </div>
                    </td>
                    {hook.useCustom ? (
                        <>
                            <td>
                                <DPFormField name="variableInterest" inline={false}>
                                    <DPInputNumber name="variableInterest" precision={2} commitOnAutofill />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="fixedInterest" inline={false}>
                                    <DPInputNumber name="fixedInterest" precision={2} commitOnAutofill />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="dealerCommitmentCommissionMin" inline={false}>
                                    <DPInputNumber
                                        name="dealerCommitmentCommissionMin"
                                        precision={0}
                                        disabled={disableCommitmentCommission}
                                        commitOnAutofill
                                    />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="dealerCommitmentCommissionPct" inline={false}>
                                    <DPInputNumber
                                        name="dealerCommitmentCommissionPct"
                                        precision={2}
                                        disabled={disableCommitmentCommission}
                                        commitOnAutofill
                                    />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="documentFee" inline={false}>
                                    <DPInputNumber
                                        name="documentFee"
                                        precision={0}
                                        disabled={disableDocumentFee}
                                        commitOnAutofill
                                    />
                                </DPFormField>
                            </td>
                            <td colSpan={2}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <DPButton
                                        type="button"
                                        size="mini"
                                        onClick={() => {
                                            hook.save({
                                                ...formik.values,
                                                dealerCommitmentCommissionMin: disableCommitmentCommission
                                                    ? 0
                                                    : formik.values.dealerCommitmentCommissionMin,
                                                dealerCommitmentCommissionPct: disableCommitmentCommission
                                                    ? 0
                                                    : formik.values.dealerCommitmentCommissionPct,
                                                documentFee: disableDocumentFee
                                                    ? 0
                                                    : formik.values.documentFee,
                                            })
                                                .then(() => toast.success($t('saved')))
                                        }}
                                        disabled={
                                            isSaveButtonDisable(
                                                isPricingUnchanged(initialValues, formik.values) && !hook.isDirty,
                                                Object.keys(formik.errors).length !== 0)
                                        }
                                    >
                                        {$t('save')}
                                    </DPButton>
                                    {hook.pricing && canBeDeleted(hook.pricing) && (
                                        <Icon
                                            link
                                            name="delete"
                                            onClick={() => {
                                                hook.deleteCustom()
                                                    .then(() => toast.success($t('deleted')))
                                            }}
                                        />
                                    )}
                                </div>
                            </td>
                        </>
                    ) : (
                        <>
                            <td>
                                <DPFormField name="variableInterest" inline={false}>
                                    <DPInputNumber name="variableInterest" precision={2} disabled />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="fixedInterest" inline={false}>
                                    <DPInputNumber name="fixedInterest" precision={2} disabled />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="dealerCommitmentCommissionMin" inline={false}>
                                    <DPInputNumber name="dealerCommitmentCommissionMin" precision={0} disabled />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="dealerCommitmentCommissionPct" inline={false}>
                                    <DPInputNumber name="dealerCommitmentCommissionPct" precision={2} disabled />
                                </DPFormField>
                            </td>
                            <td>
                                <DPFormField name="documentFee" inline={false}>
                                    <DPInputNumber name="documentFee" precision={0} disabled />
                                </DPFormField>
                            </td>
                            <td colSpan={2}>
                                {hook.pricing && canBeDeleted(hook.pricing) && (
                                    <Icon
                                        link
                                        name="delete"
                                        onClick={() => {
                                            hook.deleteCustom()
                                                .then(() => toast.success($t('deleted')))
                                        }}
                                    />
                                )}
                            </td>
                        </>
                    )}
                </tr>
            )}
        </Formik>
    )
}

export function OwnPricingPage(){
    const session = useSession()
    const toast = useToast()
    const $t = useTranslation('OwnPricingPage')
    const ownPricingApi = useApiClient(x => new OwnPricingApiClient(x))
    const purchaseAgreementPricing = useAgreementPricing(session.currentDealer?.id, 'purchase')
    const unsecureAgreementPricing = useAgreementPricing(session.currentDealer?.id, 'unsecure')
    const leasingAgreementPricing = useAgreementPricing(session.currentDealer?.id, 'leasing')

    const [dealerAgreements, setDealerAgreements] = useState<IAgreementInfo[]>([])
    const [isLoadingAgreements, setIsLoadingAgreements] = useState(false)

    const [agreementRowsBusy, setAgreementRowsBusy] = useState<Record<number, boolean>>({})
    const handleAgreementRowBusyChange = useCallback((agreementId: number, busy: boolean) => {
        setAgreementRowsBusy(prev => {
            if (prev[agreementId] === busy) return prev
            return { ...prev, [agreementId]: busy }
        })
    }, [])
    const isAnyAgreementRowBusy = Object.values(agreementRowsBusy).some(Boolean)

    useEffect(() => {
        if (!session.currentDealer?.id) return
        setIsLoadingAgreements(true)
        ownPricingApi.getDealerPurchaseAgreements(session.currentDealer.id)
            .then(agreements => setDealerAgreements(agreements ?? []))
            .finally(() => setIsLoadingAgreements(false))
    }, [session.currentDealer?.id])

    const isPricingUnchanged = (origin: IPricing, current: IPricing) =>
        origin.variableInterest === current.variableInterest &&
        origin.fixedInterest === current.fixedInterest &&
        origin.dealerCommitmentCommissionMin === current.dealerCommitmentCommissionMin &&
        origin.dealerCommitmentCommissionPct === current.dealerCommitmentCommissionPct &&
        origin.documentFee === current.documentFee
    
    const isSaveButtonDisable = (isPricingUnchanged: boolean, hasError: boolean) =>
        isPricingUnchanged || hasError
    
    const canBeDeleted = (pricing: IPricing) => {
        return (
            pricing.variableInterest != null ||
            pricing.fixedInterest != null ||
            pricing.dealerCommitmentCommissionMin != null ||
            pricing.dealerCommitmentCommissionPct != null ||
            pricing.documentFee != null
        );
    };

    const purchaseFieldsSchema = Yup.object({
        variableInterest: Yup.number().min(-3).max(3).required().label($t('variableInterest')),
        fixedInterest: Yup.number().min(-3).max(3).required().label($t('fixedInterest')),
        dealerCommitmentCommissionMin: Yup.number().min(0).max(15000).required().label($t('dealerCommitmentCommission')),
        dealerCommitmentCommissionPct: Yup.number().min(0).max(4).required().label($t('dealerCommitmentCommission')),
        documentFee: Yup.number().min(0).max(3000).required().label($t('documentFee')),
    })

    const validationSchema = Yup.object({
        purchase: purchaseFieldsSchema,
        unsecure: Yup.object({
            variableInterest: Yup.number().min(-3).max(3).required().label($t('variableInterest')),
            dealerCommitmentCommissionMin: Yup.number().min(0).max(7000).required().label($t('dealerCommitmentCommission')),
            dealerCommitmentCommissionPct: Yup.number().min(0).max(4).required().label($t('dealerCommitmentCommission')),
            documentFee: Yup.number().min(0).max(3000).required().label($t('documentFee')),
        }),
        leasing: Yup.object({
            variableInterest: Yup.number().min(-3).max(3).required().label($t('variableInterest')),
            fixedInterest: Yup.number().min(-3).max(3).required().label($t('fixedInterest')),
            documentFee: Yup.number().min(0).max(15000).required().label($t('documentFee')),
        })
    })

    const initialValues = {
        purchase: purchaseAgreementPricing.pricing,
        unsecure: unsecureAgreementPricing.pricing,
        leasing: leasingAgreementPricing.pricing,
    }

    const isBusy =
        purchaseAgreementPricing.isBusy ||
        unsecureAgreementPricing.isBusy ||
        leasingAgreementPricing.isBusy ||
        isLoadingAgreements ||
        isAnyAgreementRowBusy
        
    const isInitialized =
        purchaseAgreementPricing.isInitialized &&
        unsecureAgreementPricing.isInitialized &&
        leasingAgreementPricing.isInitialized &&
        !isLoadingAgreements

    return(
        <>
            <PageHeader text={$t('ownPricing')}/>
            <Message warning>Bemærk ændring af egen prissætning først tager effekt på nye sager, ca. 5 min. efter at der er klikket "Gem".</Message>
            <BusyIndicator busy={isBusy}>
                {isInitialized &&
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={() => {}}
                    >
                        {props => (
                            <Form>
                                <DPTable celled>
                                    <thead>
                                        <tr>
                                            <th rowSpan={2}/>
                                            <th>
                                                {$t('variableInterest')}
                                            </th>
                                            <th>
                                                {$t('fixedInterest')}
                                            </th>
                                            <th colSpan={2}>
                                                {$t('dealerCommitmentCommission')}
                                            </th>
                                            <th>
                                                {$t('documentFee')}
                                            </th>
                                            <th rowSpan={2} colSpan={2}/>
                                        </tr>
                                        <tr>
                                            {/* Apply custom styles to add missing left border (bug in SematicUI) */}
                                            <th className={styles['border-left']}>
                                                {$t('addOn')}
                                            </th>
                                            <th>
                                                {$t('addOn')}
                                            </th>
                                            <th>
                                                Min kr.
                                            </th>
                                            <th>
                                                %
                                            </th>
                                            <th>
                                                Kr.
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className={styles['tbody']}>
                                        <tr>
                                            <td>
                                                <b>{$t('purchase')}</b>
                                            </td>
                                            <td>
                                                <DPFormField name="purchase.variableInterest" inline={false}>
                                                    <DPInputNumber name="purchase.variableInterest" precision={2} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="purchase.fixedInterest" inline={false}>
                                                    <DPInputNumber name="purchase.fixedInterest" precision={2} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="purchase.dealerCommitmentCommissionMin" inline={false}>
                                                    <DPInputNumber name="purchase.dealerCommitmentCommissionMin" precision={0} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="purchase.dealerCommitmentCommissionPct" inline={false}>
                                                    <DPInputNumber name="purchase.dealerCommitmentCommissionPct" precision={2} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="purchase.documentFee" inline={false}>
                                                    <DPInputNumber name="purchase.documentFee" precision={0} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td colSpan={2}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <DPButton
                                                        type="button"
                                                        size="mini"
                                                        onClick={() => {
                                                            purchaseAgreementPricing
                                                                .update(props.values.purchase!)
                                                                .then(() => toast.success($t('saved')))
                                                        }}
                                                        disabled={
                                                            isSaveButtonDisable(
                                                                isPricingUnchanged(initialValues.purchase!, props.values.purchase!),
                                                                props.errors.purchase !== undefined)
                                                        }
                                                    >
                                                        {$t('save')}
                                                    </DPButton>
                                                    {canBeDeleted(props.values.purchase!) && (
                                                        <Icon
                                                            link
                                                            name="delete"
                                                            onClick={() => {
                                                                purchaseAgreementPricing
                                                                    .clear(props.values.purchase!)
                                                                    .then(() => toast.success($t('deleted')));
                                                            }}
                                                        /> 
                                                    )}
                                                </div>
                                            </td>                                          
                                        </tr>

                                        {dealerAgreements.map(agreement => (
                                            <AgreementPricingRow
                                                key={agreement.id}
                                                dealerId={session.currentDealer!.id}
                                                agreement={agreement}
                                                categoryPricing={props.values.purchase}
                                                onBusyChange={handleAgreementRowBusyChange}
                                                validationSchema={purchaseFieldsSchema}
                                                isPricingUnchanged={isPricingUnchanged}
                                                isSaveButtonDisable={isSaveButtonDisable}
                                                canBeDeleted={canBeDeleted}
                                            />
                                        ))}

                                        <tr>
                                            <td>
                                                <b>{$t('unsecure')}</b>
                                            </td>
                                            <td>
                                                <DPFormField name="unsecure.variableInterest" inline={false}>
                                                    <DPInputNumber name="unsecure.variableInterest" precision={2} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="unsecure.fixedInterest" inline={false}>
                                                    <DPInputNumber name="unsecure.fixedInterest" disabled />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="unsecure.dealerCommitmentCommissionMin" inline={false}>
                                                    <DPInputNumber name="unsecure.dealerCommitmentCommissionMin" precision={0} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="unsecure.dealerCommitmentCommissionPct" inline={false}>
                                                    <DPInputNumber name="unsecure.dealerCommitmentCommissionPct" precision={2} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="unsecure.documentFee" inline={false}>
                                                    <DPInputNumber name="unsecure.documentFee"  precision={0} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td colSpan={2}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <DPButton
                                                        type="button"
                                                        size="mini"
                                                        onClick={() =>
                                                            unsecureAgreementPricing
                                                                .update(props.values.unsecure!)
                                                                .then(() => toast.success($t('saved')))
                                                        }
                                                        disabled={
                                                            isSaveButtonDisable(
                                                                isPricingUnchanged(initialValues.unsecure!, props.values.unsecure!),
                                                                props.errors.unsecure !== undefined)
                                                        }
                                                    >
                                                        {$t('save')}
                                                    </DPButton>
                                                    {canBeDeleted(props.values.unsecure!) && (
                                                        <Icon
                                                            link
                                                            name="delete"
                                                            onClick={() => {
                                                                unsecureAgreementPricing
                                                                    .clear(props.values.unsecure!)
                                                                    .then(() => toast.success($t('deleted')));
                                                            }}
                                                        /> 
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        <tr>
                                            <td>
                                                <b>{$t('leasing')}</b>
                                            </td>
                                            <td>
                                                <DPFormField name="leasing.variableInterest" inline={false}>
                                                    <DPInputNumber name="leasing.variableInterest" precision={2} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="leasing.fixedInterest" inline={false}>
                                                    <DPInputNumber name="leasing.fixedInterest" precision={2} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="leasing.dealerCommitmentCommissionMin" inline={false}>
                                                    <DPInputNumber name="leasing.dealerCommitmentCommissionMin" precision={0} disabled />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="leasing.dealerCommitmentCommissionPct" inline={false}>
                                                    <DPInputNumber name="leasing.dealerCommitmentCommissionPct" precision={2} disabled />
                                                </DPFormField>
                                            </td>
                                            <td>
                                                <DPFormField name="leasing.documentFee" inline={false} tooltip='Establishment fee'>
                                                    <DPInputNumber name="leasing.documentFee" precision={0} commitOnAutofill />
                                                </DPFormField>
                                            </td>
                                            <td colSpan={2}>
                                                <DPButton
                                                    type="button"
                                                    size="mini"
                                                    onClick={() => {
                                                        leasingAgreementPricing
                                                            .update(props.values.leasing!)
                                                            .then(() => toast.success($t('saved')))
                                                    }}
                                                    disabled={
                                                        isSaveButtonDisable(
                                                            isPricingUnchanged(initialValues.leasing!, props.values.leasing!),
                                                            props.errors.leasing !== undefined)
                                                    }
                                                >
                                                    {$t('save')}
                                                </DPButton>
                                            </td>
                                        </tr>
                                    </tbody>
                                </DPTable>
                            </Form>
                        )}
                    </Formik>
                }
            </BusyIndicator>
        </>
    )
}
